<template>
  <header class="hdr">
    <div class="hdr__inner">
      <router-link to="/" class="hdr__brand">
        <img class="hdr__logo" :src="logoUrl" alt="Idoing" width="28" height="28" />
        <span class="hdr__name">Idoing</span>
        <span class="hdr__tag">API</span>
      </router-link>
      <nav class="hdr__nav">
        <router-link
          v-for="item in links"
          :key="item.to"
          :to="item.to"
          class="hdr__link"
          active-class="is-active"
        >
          {{ item.label }}
        </router-link>
      </nav>

      <div class="hdr__auth">
        <template v-if="loggedIn">
          <router-link class="hdr__profile" to="/recharge" :title="profileTitle">
            <span class="hdr__nick">{{ nickname }}</span>
            <span class="hdr__sep">·</span>
            <span class="hdr__bal">
              余额
              <strong class="mono">{{ balanceText }}</strong>
            </span>
          </router-link>
          <button type="button" class="btn btn-ghost hdr__cta" @click="onLogout">退出</button>
        </template>
        <router-link v-else to="/login" class="btn btn-primary hdr__cta">登录</router-link>
      </div>
    </div>
  </header>
</template>

<script>
import { isLoggedIn, getUserLabel, clearAuth, getUser } from '../utils/auth'
import { BRAND_LOGO_URL } from '../utils/brand'
import { fetchCreditsUserProfile } from '../utils/creditsRechargeApi'

export default {
  name: 'AppHeader',
  data() {
    return {
      logoUrl: BRAND_LOGO_URL,
      loggedIn: isLoggedIn(),
      nickname: getUserLabel() || '用户',
      score: null,
      balanceLoading: false,
      links: [
        { to: '/type/all', label: '所有产品' },
        { to: '/docs', label: '文档' },
        { to: '/playground', label: '调试' },
        { to: '/usage', label: '消耗明细' },
        { to: '/recharge', label: '充值' },
        { to: '/keys', label: '密钥' }
      ]
    }
  },
  computed: {
    balanceText() {
      if (this.balanceLoading && this.score == null) return '…'
      if (this.score == null) return '—'
      return Number(this.score).toLocaleString('en-US')
    },
    profileTitle() {
      return `${this.nickname} · 余额 ${this.balanceText}`
    }
  },
  watch: {
    $route() {
      this.refreshAuth()
    }
  },
  mounted() {
    this.refreshAuth()
    window.addEventListener('storage', this.refreshAuth)
    window.addEventListener('opengate-balance-refresh', this.loadBalance)
  },
  beforeDestroy() {
    window.removeEventListener('storage', this.refreshAuth)
    window.removeEventListener('opengate-balance-refresh', this.loadBalance)
  },
  methods: {
    pickNickname(profile) {
      const cached = getUser()
      const fromCache = getUserLabel()
      const fromApi = profile && (profile.name || profile.nickname)
      return String(fromApi || fromCache || (cached && cached.phone) || '用户').trim() || '用户'
    },
    refreshAuth() {
      this.loggedIn = isLoggedIn()
      if (!this.loggedIn) {
        this.nickname = '用户'
        this.score = null
        return
      }
      this.nickname = this.pickNickname(null)
      this.loadBalance()
    },
    async loadBalance() {
      if (!isLoggedIn()) return
      this.balanceLoading = true
      try {
        const p = await fetchCreditsUserProfile()
        if (p) {
          this.nickname = this.pickNickname(p)
          this.score = Number(p.score)
          if (!Number.isFinite(this.score)) this.score = 0
        }
      } catch (e) {
        if (this.score == null) this.score = null
      } finally {
        this.balanceLoading = false
      }
    },
    onLogout() {
      clearAuth()
      this.refreshAuth()
      if (this.$route.meta && this.$route.meta.requiresAuth) {
        this.$router.push({ path: '/login', query: { redirect: this.$route.fullPath } })
      }
    }
  }
}
</script>

<style scoped>
.hdr {
  position: sticky;
  top: 0;
  z-index: 40;
  border-bottom: 1px solid var(--line);
  background: rgba(11, 15, 14, 0.78);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
}
.hdr__inner {
  max-width: 1120px;
  margin: 0 auto;
  padding: 14px 20px;
  display: flex;
  align-items: center;
  gap: 18px;
}
.hdr__brand {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin-right: auto;
}
.hdr__logo {
  width: 28px;
  height: 28px;
  object-fit: contain;
  display: block;
  border-radius: 6px;
  background: #0a0a0a;
}
.hdr__name {
  font-family: var(--font-display);
  font-weight: 800;
  font-size: 18px;
  letter-spacing: -0.03em;
}
.hdr__tag {
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 999px;
  border: 1px solid var(--line-strong);
  color: var(--muted);
}
.hdr__nav {
  display: none;
  gap: 4px;
}
.hdr__link {
  padding: 8px 12px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  color: var(--muted);
}
.hdr__link:hover,
.hdr__link.is-active {
  color: var(--text);
  background: rgba(255, 255, 255, 0.05);
}
.hdr__auth {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}
.hdr__profile {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  max-width: 220px;
  padding: 4px 2px;
  font-size: 12px;
  color: var(--muted);
  text-decoration: none;
}
.hdr__profile:hover {
  color: var(--text);
}
.hdr__profile:hover .hdr__bal strong {
  color: var(--accent);
}
.hdr__nick {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 88px;
  font-weight: 600;
  color: var(--text);
}
.hdr__sep {
  opacity: 0.45;
}
.hdr__bal {
  white-space: nowrap;
}
.hdr__bal strong {
  margin-left: 4px;
  font-weight: 700;
  color: var(--accent);
}
.hdr__cta {
  padding: 8px 14px;
  font-size: 13px;
}
@media (min-width: 800px) {
  .hdr__nav {
    display: flex;
  }
  .hdr__profile {
    max-width: 280px;
  }
  .hdr__nick {
    max-width: 120px;
  }
}
</style>
